import React, { useState, useEffect } from 'react';

import Accordion from 'b2b-ui-components-lib/lib/components/accordion';
import Input from 'b2b-ui-components-lib/lib/components/input';
import Datepicker from 'b2b-ui-components-lib/lib/components/datePicker';

import FileUpload from 'home/view/manage-feasibility/pages/DedicatedOrderSummary/FileUpload';
import { getDefaultValue, getDefaultFiles, ignoreKeys, loiExpiryDate } from './FormControl';

import { isFormValid } from 'common/utils/validator';
import { DOCUMENT_TYPE_ENUM } from 'home/view/manage-feasibility/constants';
import { MULTI_SITE_MODULE_ID, SUMMARY_AUTH_TOKEN, ERROR_TOAST, DEFAULT_TOAST_MSG, SHORT_CB_DELAY } from 'home/view/manage-feasibility/constants';
import { deleteDocumentsForSummary } from 'home/view/manage-feasibility/services';
import { getFromSessionStorage, getPreviousYearDate, getRange } from 'common/utils/app.utils';
import { FILE_MAX_SIZE, FILE_MAX_SIZE_MSG, PO_MAX_YEAR_BACK } from 'common/constants/app.constants';

const LoiDetails = ({ order = {}, updateParentForm, setFormStatus, updateOrder = () => {}, setToast = () => {}, isOrderConfirmation = false }) => {
  const fileSchema = {
    supportedTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
    maxFileLimit: FILE_MAX_SIZE,
    documentNames: ['LOI_DOCUMENT', 'LOI_DOCUMENT_2', 'LOI_DOCUMENT_3'],
    documentType: DOCUMENT_TYPE_ENUM.LOI,
    allowMultiplePDFs: true
  };

  const defaultFormState = getDefaultValue(order);

  const [form, setForm] = useState(defaultFormState);
  const [accordionMetadata, setAccordionMetadata] = useState({
    isValidated: false,
    isExpanded: true
  });
  const [defaultFileArr, setDefaultFileArr] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    setDefaultFileArr(getDefaultFiles(order.documents));
  }, [order]);

  const onLoiOrderNo = (value) => {
    setForm({
      ...form,
      loiNumber: {
        ...form.loiNumber,
        value,
        touched: true,
        errors: form.loiNumber.validator(value)
      }
    });
  };

  const onLoiOrderDateChange = (value, keyName) => {
    if (value) {
      setForm({
        ...form,
        [keyName]: { ...form[keyName], value, touched: true, errors: [] }
      });
      return;
    }
    setForm({
      ...form,
      [keyName]: {
        ...form[keyName],
        value,
        touched: true,
        errors: keyName === loiExpiryDate ? [] : ['This field is mandatory']
      }
    });
  };

  const onLoiOrderTotalCharges = (value) =>
    setForm({
      ...form,
      loiAmount: {
        ...form.loiAmount,
        value,
        touched: true,
        errors: form.loiAmount.validator(value)
      }
    });

  const onFileUpload = (files, type, uploadedFiles) => {
    setForm({ ...form, files });
    if (files) {
      setUploadedFiles(uploadedFiles);
    } else {
      setUploadedFiles([]);
    }
  };

  const onFieldBlur = (formElement, keyName) => {
    const { value, validator, errors: existingError } = formElement;
    let errors = [];
    if (typeof validator === 'function') {
      errors = validator(value);
    }
    setForm({
      ...form,
      [keyName]: {
        ...form[keyName],
        value,
        errors: errors?.concat(existingError),
        touched: true
      }
    });
  };

  useEffect(() => {
    const poAmount = form && form.loiAmount && form.loiAmount.value;
    const poNumber = form && form.loiNumber && form.loiNumber.value;
    const loiDate = form && form.loiDate && form.loiDate.value;
    const fileUrl = (form && form.files.value) || (form && form.files === 'success');

    const isValid = !!(poAmount && poNumber && loiDate && fileUrl);

    if (isFormValid(form, ignoreKeys)) {
      setAccordionMetadata({ isValidated: true, isExpanded: true });
      updateParentForm({ loiDetail: form });
      setFormStatus({ loiDetail: true });
    } else if (isValid) {
      setAccordionMetadata({
        isValidated: isValid,
        isExpanded: isValid
      });
      updateParentForm({ loiDetail: form });
      setFormStatus({ loiDetail: true });
    } else {
      const hasValue = !!(poAmount || poNumber || loiDate || fileUrl);

      setAccordionMetadata({
        isValidated: false,
        isExpanded: hasValue
      });
      updateParentForm({ loiDetail: null });
      setFormStatus({ loiDetail: false });
    }

    let timeoutId;
    if (isFormValid(form, ignoreKeys)) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => updateOrder(form, false, true, true), SHORT_CB_DELAY);
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        updateOrder(form, false, false, true);
        deleteUploadedFile();
      }, SHORT_CB_DELAY);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [form]);

  const deleteUploadedFile = () => {
    const summaryAuthToken = getFromSessionStorage(MULTI_SITE_MODULE_ID, SUMMARY_AUTH_TOKEN);

    Array.isArray(uploadedFiles) &&
      uploadedFiles.forEach(async (_, index) => {
        const documentName = fileSchema.documentNames[index];
        const payload = { documentType: fileSchema.documentType, documentName };

        try {
          const { res, err } = await deleteDocumentsForSummary(payload, summaryAuthToken);

          if (res) {
            setUploadedFiles([]);
            setDefaultFileArr([]); // clear default files also after checkbox unchecked in PO details form
          } else if (err) {
            const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

            setToast(true, errorMsg, ERROR_TOAST);

            throw new Error(errorMsg);
          }
        } catch (e) {
          console.error(e);
        }
      });
  };

  return (
    <React.Fragment>
      <Accordion
        title="Letter Of Intent"
        autoScrollOnExpand={false}
        isExpanded={true}
        isValidated={accordionMetadata.isValidated}
        isEditable={accordionMetadata.isEditable}
        onEditClick={() =>
          setAccordionMetadata(({ isEditable }) => ({
            ...accordionMetadata,
            isEditable: !isEditable
          }))
        }
      >
        <div className="accordion_body">
          <div className="order_details_form">
            <div className="row field_row">
              <div className="flg-lg-12 flg-md-12">
                <div className="form_area">
                  <div className="row">
                    <div className="flg-lg-6 flg-md-6">
                      <Input
                        label="Letter Of Intent Number"
                        required
                        onBlur={() => onFieldBlur(form.loiNumber, 'loiNumber')}
                        errors={form.loiNumber.errors}
                        touched={form.loiNumber.touched}
                        value={form.loiNumber.value}
                        onChange={onLoiOrderNo}
                        disabled={isOrderConfirmation}
                      />
                    </div>

                    <div className="flg-lg-6 flg-md-6">
                      <Datepicker
                        name="datepicker"
                        label="Letter Of Intent Date"
                        required={true}
                        errors={form.loiDate.errors}
                        touched={form.loiDate.touched}
                        value={form.loiDate.value}
                        onChange={(value) => onLoiOrderDateChange(value, 'loiDate')}
                        maxDate={new Date()}
                        minDate={getPreviousYearDate(PO_MAX_YEAR_BACK)}
                        years={getRange(getPreviousYearDate(PO_MAX_YEAR_BACK).getFullYear(), new Date().getFullYear())}
                        disabled={isOrderConfirmation}
                      />
                    </div>

                    <div className="flg-lg-6 flg-md-6">
                      <Input
                        label="Total Charges"
                        required
                        onBlur={() => onFieldBlur(form.loiAmount, 'loiAmount')}
                        errors={form.loiAmount.errors}
                        touched={form.loiAmount.touched}
                        value={form.loiAmount.value}
                        onChange={onLoiOrderTotalCharges}
                        disabled={isOrderConfirmation}
                      />
                    </div>

                    <div className="flg-lg-6 flg-md-6">
                      <Datepicker
                        name="datepicker"
                        label="Letter Of Intent Expiry (Optional)"
                        required={true}
                        errors={form.loiExpiryDate.errors}
                        touched={form.loiExpiryDate.touched}
                        value={form.loiExpiryDate.value}
                        onChange={(value) => onLoiOrderDateChange(value, 'loiExpiryDate')}
                        maxDate={new Date(new Date().setYear(new Date().getFullYear() + 5))}
                        minDate={new Date()}
                        years={getRange(new Date().getFullYear(), new Date().getFullYear() + 5)}
                        disabled={isOrderConfirmation}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="accordion_footer">
          <FileUpload
            onFileUploadSuccess={onFileUpload}
            label="Upload LOI Order Document"
            supportedLabel="Supported file format is .PDF, .PNG, .JPG"
            limitLabel={FILE_MAX_SIZE_MSG}
            fileSchema={fileSchema}
            defaultFiles={defaultFileArr}
            setToast={setToast}
            isOrderConfirmation={isOrderConfirmation}
            multiple={true}
          ></FileUpload>
        </div>
      </Accordion>
    </React.Fragment>
  );
};

export default LoiDetails;
